import { environment } from 'src/environments/environment';

// Feature flags read from the active environment file.
//
// `useNewLibrary` toggles the AI-search library (WeblibraryNewAiSearchComponent)
// in place of the classic one. It is set to `true` ONLY in the dev environments
// (environment.web.dev.ts, environment.web.localdev.ts); every other env file
// omits the key entirely, so the flag reads `false` there — production keeps the
// classic library, byte-identical to 2b0caa02. The optional cast lets the key be
// absent without a compile error (environment is an untyped literal).
export const useNewLibrary = (environment as { useNewLibrary?: boolean }).useNewLibrary === true;

// `assetQueries` shows the Asset-Queries preview on /admin/content: what a separate group
// of per-asset queries would contain, rendered from the catalogue descriptions. It creates
// nothing — see tools/asset-queries/build-proposal.mjs. Dev environments only, same as
// above: every other env file omits the key, so the tab does not exist in production.
export const assetQueries = (environment as { assetQueries?: boolean }).assetQueries === true;

// `hierarchyLab` mounts /admin/hierarchy: a frontend-only lab where the whole catalogue is laid
// out as World → Object class → Query → Asset and can be edited against a localStorage mock of
// the backend fields that do not exist yet. See docs/hierarchy-lab-plan.md. Dev only.
export const hierarchyLab = (environment as { hierarchyLab?: boolean }).hierarchyLab === true;

// `hierarchyQueries` adds a second preview to /admin/content: the Hierarchy Lab snapshot drawn
// as ordinary groups and query rows, so the new structure can be judged in the tree people
// actually work in rather than on a page of its own. It reads the same
// src/assets/hierarchy/hierarchy-lab.json the lab reads and creates nothing — see
// src/app/admin/utils/hierarchy-mock.ts. Dev only, same as above.
export const hierarchyQueries = (environment as { hierarchyQueries?: boolean }).hierarchyQueries === true;

// `discoverLanding` mounts /discover: the reference-discovery landing ported from the boss's
// prototype (see docs/landing-discover-port.md). Static copy, six hardcoded sample references,
// the search wiring comes in the next iteration. Dev only, same mechanism as above.
export const discoverLanding = (environment as { discoverLanding?: boolean }).discoverLanding === true;

// `renderLanding` mounts /render: the render product's landing and its upload → preview →
// purchase → download flow, ported from the boss's prototype (docs/render-landing-port.md).
// Everything past the drop zone is simulated in the browser until the backend exists. Dev only,
// same mechanism as above; the discover topbar's "render" link points here only when it is on.
export const renderLanding = (environment as { renderLanding?: boolean }).renderLanding === true;

// `renderOnly` — the site is the render product and nothing else, for now: a test of the demand for
// it (owner, 2026-10-23). The /render landing is the home page at the site's root, the flow keeps its
// /render/… addresses, and the old site — library, model and collection pages, the store, the old
// landings, /discover — is not routed at all; every other address leads home. Only the legal pages,
// /admin and, where debug pages are shown, /service stay. Its components are kept as they are, so
// switching the flag off brings the old site back. Implies the render routes whatever
// `renderLanding` says. Old-site subscribers cancel through support (owner, same day).
// On in environment.web.localdev.ts, environment.web.dev.ts and environment.web.prod.ts (prod since
// Yahor's render backend went there, 2026-11-02), with `renderLive`.
//
// The old site back (the boss may ask for it): `renderOnly: false` in those environments (keep
// `renderLanding` and `renderLive` for /render at its old address), and in
// src/environments/index-web/index.html swap each RENDER-ONLY line for the commented one under it —
// the black page, the iOS app banner and the YouTube player API. `grep -rn RENDER-ONLY src` finds
// them all.
export const renderOnly = (environment as { renderOnly?: boolean }).renderOnly === true;

// `renderLive` — the /render flow on the render backend, for real (owner, 2026-10-01; called
// `renderSimulator` until 2026-11-02): uploads go to the app's bucket (models-and-scenes-dev /
// -prod) like the library's and start real GPU renders — the preview-only run, then, paid for
// through Stripe's checkout (2026-10-14), the videos — and the page follows the model's Firestore
// document the way the library does; the platform pack is the backend's ZIP. Off: everything is
// MockRenderBackend's, in the browser. On in environment.web.localdev.ts, environment.web.dev.ts and
// environment.web.prod.ts. See docs/render-landing-port.md.
export const renderLive = (environment as { renderLive?: boolean }).renderLive === true;

// `renderFullQuality` — the /render landing on the designer's 1080p renders (2026-11-03): the hero and
// the bands play page copies of them (render-v2.data.ts), and each band offers "Full quality", the
// render's own file as a customer gets it, in a viewer over the page. The files are on the dev
// landing bucket only (owner, 2026-11-03); copy `_render-hq/<render>/` and `_render-web/<render>/`
// to landing-prod-tf before switching it on in production. Off: the October renders, no viewer.
// On in environment.web.localdev.ts and environment.web.dev.ts.
export const renderFullQuality = (environment as { renderFullQuality?: boolean }).renderFullQuality === true;

// `gaMeasurementId` — Google Analytics 4, production only (owner, 2026-11-03): set in
// environment.web.prod.ts and nowhere else, so every other build has no banner, no gtag script and no
// request to Google — not even the ID in its bundle. Behind the cookie banner: nothing of Google's
// loads until the visitor accepts (AnalyticsService, CookieConsentComponent).
export const gaMeasurementId = (environment as { gaMeasurementId?: string }).gaMeasurementId || null;

// `productAnalytics` — our own record of what signed-in users do in the render flow (owner,
// 2026-11-03): ProductAnalyticsService sends their steps to the trackProductEvents function, which
// keeps them in Firestore for /admin/analytics. Where the cookie banner exists (gaMeasurementId), only
// after the visitor accepts analytics. On in localdev, dev and production (owner, 2026-11-03).
export const productAnalytics = (environment as { productAnalytics?: boolean }).productAnalytics === true;

// `renderFrames` — the frame count a /render upload asks the full render for, instead of the
// service settings' (900 by default: 15 s). Prod: 720, the 12 s the page promises
// (RENDER_OUTPUT_SPEC; the turn spans whatever count is asked). Dev: 120, so a paid run ends in
// minutes (owner, 2026-10-05). It goes with the model file at upload; a model
// already uploaded keeps its count. Absent: the settings' count, as the library sends it. A
// `frameNumber` set by hand in localStorage (service settings) overrides it (owner, 2026-10-20).
export const renderFrames = Number((environment as { renderFrames?: number }).renderFrames) || null;
